import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { CartProvider, useCart } from './CartContext';
import Navbar from './components/Navbar';
import HeroSlider from './components/HeroSlider';
import FeaturedCategories from './components/FeaturedCategories';
import PromoBanners from './components/PromoBanners';
import PopularProducts from './components/PopularProducts';
import DailyDeals from './components/DailyDeals';
import Features from './components/Features';
import Footer from './components/Footer';
import CartSidebar from './components/CartSidebar';
import CheckoutPage from './components/CheckoutPage';
import B2BHomePage from './components/B2BHomePage';
import B2BProductsPage from './components/B2BProductsPage';
import CategoryProductsPage from './components/CategoryProductsPage';
import { Toast, FloatingCartButton } from './components/CartExtras';
import Register from './components/Register';
import Login from './components/Login';
import SearchResultsPage from './components/SearchResultsPage';
import { MyOrdersPage, MyAddressesPage, MyProfilePage } from './components/UserPages';
import PaymentSuccess from './components/PaymentSuccess';
import AlertModal, { showAlert, showError, showSuccess } from './components/AlertModal';
import AdminDashboard from './components/AdminDashboard';
import { AboutPage, ContactPage } from './components/InfoPages';

// Location Modal Component
function LocationModal({ onClose }) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLocation = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            setLocation({
              address: data.display_name,
              city: data.address?.city || data.address?.town,
              pincode: data.address?.postcode
            });
          } catch (error) {
            console.error('Error:', error);
            setLocation({
              address: `${latitude}, ${longitude}`,
              coordinates: { lat: latitude, lng: longitude }
            });
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error:', error);
          setLoading(false);
          showError('Unable to fetch location. Please check your permissions.');
        }
      );
    } else {
      showError('Geolocation is not supported by your browser');
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        maxWidth: '500px',
        width: '90%',
        padding: '2rem',
        position: 'relative'
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: 'none',
          border: 'none',
          fontSize: '1.5rem',
          cursor: 'pointer'
        }}>×</button>

        <h3>Set Delivery Location</h3>
        
        {!location && !loading && (
          <button
            onClick={fetchLocation}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            📍 Detect My Location
          </button>
        )}

        {loading && <p>Fetching your location...</p>}

        {location && (
          <div style={{ marginTop: '1rem' }}>
            <p><strong>📍 Location Detected:</strong></p>
            <p>{location.address}</p>
            <p><strong>City:</strong> {location.city}</p>
            <p><strong>Pincode:</strong> {location.pincode}</p>
            <button
              onClick={() => {
                localStorage.setItem('userLocation', JSON.stringify(location));
                window.dispatchEvent(new Event('location-updated'));
                showSuccess('Location saved successfully!');
                onClose();
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Confirm Location
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CartSidebarWithNav() {
  const navigate = useNavigate();
  return <CartSidebar onCheckout={() => navigate('/checkout')} />;
}

// B2C Home Page Component
function B2CHomePage() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const { userType } = useCart();

  // Keep mode and home page in sync
  if (userType === 'b2b') {
    return <Navigate to="/b2b" replace />;
  }

  const handleRegisterSuccess = (userData) => {
    console.log('Registration successful:', userData);
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
  };

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <main>
        <HeroSlider />
        <FeaturedCategories />
        <PromoBanners />
        <PopularProducts />
        <DailyDeals />
        <Features />
      </main>
      <Footer />

      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login 
          onClose={() => setLoginOpen(false)} 
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      
      {signupOpen && (
        <Register 
          onClose={() => setSignupOpen(false)} 
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}
      
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

// B2B Home Page Wrapper
function B2BHomePageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const { userType, switchUserType } = useCart();

  // Sync mode before paint so the B2B toggle/nav never flash as B2C on /b2b
  useLayoutEffect(() => {
    if (userType !== 'b2b') switchUserType('b2b', { silent: true });
  }, [userType, switchUserType]);

  const handleRegisterSuccess = (userData) => {
    console.log('Registration successful:', userData);
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
  };

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <main>
        <B2BHomePage />
      </main>
      <Footer />

      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login 
          onClose={() => setLoginOpen(false)} 
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      
      {signupOpen && (
        <Register 
          onClose={() => setSignupOpen(false)} 
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}
      
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

// B2B Products Page Wrapper
function B2BProductsPageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleRegisterSuccess = (userData) => {
    console.log('Registration successful:', userData);
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
  };

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <main>
        <B2BProductsPage />
      </main>
      <Footer />

      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login 
          onClose={() => setLoginOpen(false)} 
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      
      {signupOpen && (
        <Register 
          onClose={() => setSignupOpen(false)} 
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}
      
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

// Search Results Page Wrapper
function SearchResultsPageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleRegisterSuccess = (userData) => {
    console.log('Registration successful:', userData);
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
  };

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <main>
        <SearchResultsPage />
      </main>
      <Footer />

      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {signupOpen && (
        <Register
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}

      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

// Category Products Page Wrapper
function CategoryProductsPageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleRegisterSuccess = (userData) => {
    console.log('Registration successful:', userData);
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
  };

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <main>
        <CategoryProductsPage />
      </main>
      <Footer />

      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login 
          onClose={() => setLoginOpen(false)} 
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      
      {signupOpen && (
        <Register 
          onClose={() => setSignupOpen(false)} 
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={handleRegisterSuccess}
        />
      )}
      
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

function MyOrdersPageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <MyOrdersPage />
      <Footer />
      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={() => {}}
        />
      )}
      {signupOpen && (
        <Register
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={() => {}}
        />
      )}
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

function MyAddressesPageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <MyAddressesPage />
      <Footer />
      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={() => {}}
        />
      )}
      {signupOpen && (
        <Register
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={() => {}}
        />
      )}
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

function MyProfilePageWrapper() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <MyProfilePage />
      <Footer />
      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={() => {}}
        />
      )}
      {signupOpen && (
        <Register
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={() => {}}
        />
      )}
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

function InfoPageWrapper({ children }) {
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  return (
    <>
      <Navbar
        onOpenSignup={() => setSignupOpen(true)}
        onOpenLogin={() => setLoginOpen(true)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      {children}
      <Footer />
      <CartSidebarWithNav />
      <FloatingCartButton />
      <Toast />

      {loginOpen && (
        <Login
          onClose={() => setLoginOpen(false)}
          onSwitchToRegister={() => {
            setLoginOpen(false);
            setSignupOpen(true);
          }}
          onLoginSuccess={() => {}}
        />
      )}
      {signupOpen && (
        <Register
          onClose={() => setSignupOpen(false)}
          onSwitchToLogin={() => {
            setSignupOpen(false);
            setLoginOpen(true);
          }}
          onRegisterSuccess={() => {}}
        />
      )}
      {locationOpen && <LocationModal onClose={() => setLocationOpen(false)} />}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <AlertModal />
        <ModeRouteSync />
        <Routes>
          <Route path="/" element={<B2CHomePage />} />
          <Route path="/b2b" element={<B2BHomePageWrapper />} />
          <Route path="/b2b/products/:categoryName/:categoryId" element={<B2BProductsPageWrapper />} />
          <Route path="/category/:categoryName/:categoryId" element={<CategoryProductsPageWrapper />} />
          <Route path="/search/:query" element={<SearchResultsPageWrapper />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/my-orders" element={<MyOrdersPageWrapper />} />
          <Route path="/addresses" element={<MyAddressesPageWrapper />} />
          <Route path="/profile" element={<MyProfilePageWrapper />} />
          <Route path="/about" element={<InfoPageWrapper><AboutPage /></InfoPageWrapper>} />
          <Route path="/contact" element={<InfoPageWrapper><ContactPage /></InfoPageWrapper>} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </CartProvider>
    </BrowserRouter>
  );
}

/** Keep B2C/B2B toggle in sync with the current route */
function ModeRouteSync() {
  const location = useLocation();
  const { userType, switchUserType } = useCart();

  useLayoutEffect(() => {
    const onB2B = location.pathname === '/b2b' || location.pathname.startsWith('/b2b/');
    const onB2CHome =
      location.pathname === '/' ||
      location.pathname.startsWith('/category/');

    if (onB2B && userType !== 'b2b') {
      switchUserType('b2b', { silent: true });
    } else if (onB2CHome && userType !== 'b2c') {
      switchUserType('b2c', { silent: true });
    }
  }, [location.pathname, userType, switchUserType]);

  return null;
}